import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import {
  GetFunctionsByEventId, GetGuestsByEventId, GetCardTypes, GetExpenses,
  GetServicesByEventId,
} from '../../api/api';
import {
  fmtDate, fmtTime, money, needsCard, isPastFunction, EXPENSE_CATEGORIES,
} from '../../utils/helpers';
import {
  functionHeadcount, rowFunctionIds, householdKey, extraPersonsFor,
} from '../../utils/headcount';

// BRIEF REPORT — the one sheet an organiser carries around:
//   1  how many people at each function      (functionHeadcount - the shared rule)
//   2  who invited how many                  (per member, and per function)
//   3  how many cards to order for distribution
//   4  what is paid in advance and what is still to be paid
// Every block, every function and every cost category has a CHECKBOX: unticking
// one drops it from the screen AND from the PDF / Excel, so the same page gives
// "all of it" and "only the catering" without a second report.
//
// Nothing here is new data: the numbers come from the calls the Headcount,
// Cards, Expenses and Services pages already make. Admin-only - the figures
// need the whole guest list, and a member only ever sees their own guests.

const num = (v) => Number(v || 0) || 0;
const round2 = (n) => Math.round(n * 100) / 100;
const typeFnIds = (t) => String(t.FunctionIds || '').split(',').map(v => v.trim()).filter(Boolean);

// Functions in the order they run: date, then start time, then id.
const byWhen = (a, b) => String(a.FunctionDate || '').localeCompare(String(b.FunctionDate || ''))
  || String(a.StartTime || '').localeCompare(String(b.StartTime || ''))
  || Number(a.FunctionId) - Number(b.FunctionId);

export const BLOCK_LABELS = {
  members: 'Members at each function',
  invitedBy: 'Who invited how many',
  cards: 'Cards required for distribution',
  money: 'Advance paid & balance to pay',
};

export function useBrief(event) {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const canView = isAdmin;

  const [functions, setFunctions] = useState([]);
  const [guests, setGuests] = useState([]);
  const [cardTypes, setCardTypes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // What the report shows. Everything is on until it is unticked.
  const [blocks, setBlocks] = useState({ members: true, invitedBy: true, cards: true, money: true });
  const [fnOff, setFnOff] = useState(() => new Set());     // functions left out
  const [catOff, setCatOff] = useState(() => new Set());   // cost categories left out
  const [withEstimates, setWithEstimates] = useState(true);

  const toggleBlock = (key) => setBlocks(b => ({ ...b, [key]: !b[key] }));
  const toggleOff = (setter) => (key) => setter(prev => {
    const next = new Set(prev);
    const k = String(key);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const toggleFunction = toggleOff(setFnOff);
  const toggleCategory = toggleOff(setCatOff);

  const load = useCallback(async () => {
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    try {
      const [f, g, c, e, s] = await Promise.all([
        GetFunctionsByEventId({ EventId: event.EventId }),
        GetGuestsByEventId({ EventId: event.EventId }),
        GetCardTypes({ EventId: event.EventId }),
        GetExpenses({ EventId: event.EventId }),
        GetServicesByEventId({ EventId: event.EventId }),
      ]);
      setFunctions((Array.isArray(f) ? f : []).slice().sort(byWhen));
      setGuests(Array.isArray(g) ? g : []);
      setCardTypes(Array.isArray(c) ? c : []);
      setExpenses(Array.isArray(e) ? e : []);
      setServices((Array.isArray(s) ? s : []).filter(x => num(x.Valid ?? 1) !== 0 && x.Status !== 'Cancelled'));
    } catch {
      toast('Could not load the brief');
    } finally {
      setLoading(false);
    }
  }, [event, canView, toast]);
  useEffect(() => { load(); }, [load]);

  const fnOn = useCallback((id) => !fnOff.has(String(id)), [fnOff]);
  // A function whose date has passed is NOT in the brief: the sheet is carried
  // around to plan what is still to come. `functions` keeps them (a card type
  // may still name one); `liveFunctions` is what the report counts.
  const liveFunctions = useMemo(() => functions.filter(f => !isPastFunction(f)), [functions]);
  const pastFunctions = useMemo(() => functions.filter(f => isPastFunction(f)), [functions]);
  const shownFunctions = useMemo(() => liveFunctions.filter(f => fnOn(f.FunctionId)), [liveFunctions, fnOn]);
  const twoSided = useMemo(() => guests.some(g => ['Groom', 'Bride', 'Male', 'Female'].includes(g.Side)), [guests]);

  // ── 1 · Members at each function ────────────────────────────────────────
  const memberRows = useMemo(() => liveFunctions.map(f => {
    const h = functionHeadcount(guests, f.FunctionId, cardTypes);
    return {
      key: String(f.FunctionId),
      FunctionId: f.FunctionId,
      FunctionName: f.FunctionName,
      date: fmtDate(f.FunctionDate),
      time: [fmtTime(f.StartTime), fmtTime(f.EndTime)].filter(Boolean).join(' – '),
      venue: f.Venue || '',
      parties: h.parties,
      named: h.named,
      extra: h.extra,
      persons: h.persons,
      groom: h.bySide.Groom || 0,
      bride: h.bySide.Bride || 0,
      common: h.bySide.Common || 0,
      on: fnOn(f.FunctionId),
    };
  }), [liveFunctions, guests, cardTypes, fnOn]);

  const memberShown = useMemo(() => memberRows.filter(r => r.on), [memberRows]);
  const memberTotals = useMemo(() => memberShown.reduce((t, r) => ({
    parties: t.parties + r.parties, named: t.named + r.named, extra: t.extra + r.extra,
    persons: t.persons + r.persons, groom: t.groom + r.groom, bride: t.bride + r.bride,
  }), { parties: 0, named: 0, extra: 0, persons: 0, groom: 0, bride: 0 }), [memberShown]);
  const biggest = useMemo(() => memberShown.reduce((best, r) => (r.persons > (best?.persons || 0) ? r : best), null), [memberShown]);

  // ── 2 · Who invited how many ────────────────────────────────────────────
  // One line per person who invited guests. A guest row exists per function, so
  // everything is counted over DISTINCT guests; a count-only group counts the
  // persons it stands for (ExpectedCount).
  const invitedByRows = useMemo(() => {
    const people = new Map();
    const seenGuest = new Map();          // GuestId → the row kept for it
    guests.forEach(r => {
      const gid = String(r.GuestId);
      if (!seenGuest.has(gid)) seenGuest.set(gid, r);
    });
    const households = new Map();         // adder → Map(householdKey → extra cards)
    seenGuest.forEach(r => {
      const key = `${r.CreatedBy || 0}`;
      if (!people.has(key)) {
        people.set(key, {
          key,
          name: String(r.AddedByName || '').trim() || (num(r.CreatedBy) ? `User ${r.CreatedBy}` : '—'),
          mobile: r.AddedByMobile || '',
          isGuest: num(r.AddedByGuest) === 1,
          guests: 0, persons: 0, cards: 0, extraCards: 0, perFn: {},
        });
        households.set(key, new Map());
      }
      const p = people.get(key);
      const size = Math.max(1, num(r.ExpectedCount || 1));
      p.guests += 1;
      p.persons += size;
      if (needsCard(r)) {
        const hk = householdKey(r);
        const hm = households.get(key);
        const cards = num(r.ExtraCards);
        const prev = hm.get(hk);
        if (!prev || cards > prev.extraCards) hm.set(hk, { extraCards: cards, extraPersons: num(r.ExtraPersons) });
      }
      rowFunctionIds(r).forEach(id => { p.perFn[id] = (p.perFn[id] || 0) + size; });
    });
    people.forEach((p, key) => {
      const hm = households.get(key);
      p.cards = hm.size;
      hm.forEach(h => { p.extraCards += h.extraCards; p.persons += extraPersonsFor(h.extraCards, h.extraPersons); });
      p.totalCards = p.cards + p.extraCards;
    });
    return [...people.values()].sort((a, b) => b.persons - a.persons || a.name.localeCompare(b.name));
  }, [guests]);

  const invitedByTotals = useMemo(() => invitedByRows.reduce((t, r) => ({
    guests: t.guests + r.guests, persons: t.persons + r.persons, totalCards: t.totalCards + r.totalCards,
  }), { guests: 0, persons: 0, totalCards: 0 }), [invitedByRows]);

  // ── 3 · Cards required for distribution ─────────────────────────────────
  // One card per household (family, else mobile), the hosts' own family left
  // out - exactly the rule the Cards page prints by.
  const cardStats = useMemo(() => {
    const byKey = new Map();
    guests.filter(needsCard).forEach(r => {
      const k = householdKey(r);
      if (!byKey.has(k)) byKey.set(k, { fnIds: new Set(), extraCards: 0, extraPersons: 0, head: r });
      const c = byKey.get(k);
      if (num(r.FamilyId) && String(r.GuestId) === String(r.FamilyId)) c.head = r;
      rowFunctionIds(r).forEach(id => c.fnIds.add(String(id)));
      c.extraCards = Math.max(c.extraCards, num(r.ExtraCards));
      c.extraPersons = Math.max(c.extraPersons, num(r.ExtraPersons));
    });
    const list = [...byKey.values()];
    const extraCards = list.reduce((s, c) => s + c.extraCards, 0);
    const rows = cardTypes.map(t => {
      const ids = typeFnIds(t);
      const households = list.filter(c => ids.some(id => c.fnIds.has(id))).length;
      const extra = num(t.ForExtraCards) === 1 ? extraCards : 0;
      return {
        key: String(t.CardTypeId),
        name: t.CardTypeName,
        kind: t.Kind || 'Card',
        forFunctions: ids.map(id => functions.find(f => String(f.FunctionId) === id)?.FunctionName).filter(Boolean).join(', '),
        households, extra, total: households + extra,
      };
    });
    return {
      rows,
      households: list.length,
      extraCards,
      extraPersons: list.reduce((s, c) => s + extraPersonsFor(c.extraCards, c.extraPersons), 0),
      withoutType: cardTypes.length ? list.filter(c => !cardTypes.some(t => typeFnIds(t).some(id => c.fnIds.has(id)))).length : 0,
      ownFamily: new Set(guests.filter(r => !needsCard(r)).map(r => String(r.GuestId))).size,
      toOrder: rows.reduce((s, r) => s + r.total, 0),
    };
  }, [guests, cardTypes, functions]);

  // ── 4 · Advance paid & balance to pay ───────────────────────────────────
  // Expenses (Fixed, or rate × persons - live off the headcount above) PLUS the
  // engagements of the Services page, the same way the Expenses screen shows
  // them, grouped into one line per category so the sheet stays one page.
  const headcountOf = useCallback((fnId) => (fnId
    ? functionHeadcount(guests, fnId, cardTypes).persons : 0), [guests, cardTypes]);

  const moneyLines = useMemo(() => {
    const lines = [];
    expenses.forEach(e => {
      const persons = e.PersonsSource === 'Headcount' && e.PersonsFunctionId
        ? headcountOf(e.PersonsFunctionId) : num(e.Persons);
      const total = e.AmountType === 'PerPerson' ? round2(num(e.Rate) * persons) : num(e.TotalAmount);
      const paid = num(e.PaidAmount);
      lines.push({
        category: e.Category || 'Other', title: e.Title, estimate: num(e.IsEstimate) === 1,
        total, paid, balance: round2(total - paid), source: 'expense',
      });
    });
    services.forEach(sv => {
      const total = num(sv.TotalAmount);
      const paid = num(sv.AdvancePaid);      // the API keeps it equal to the payments
      lines.push({
        category: sv.ServiceType || 'Services',
        title: sv.CompanyName || sv.ContactName || sv.ServiceType || 'Service',
        estimate: sv.Status === 'Enquiry',
        total, paid, balance: round2(total - paid), source: 'service',
      });
    });
    return lines;
  }, [expenses, services, headcountOf]);

  const moneyRows = useMemo(() => {
    const m = new Map();
    moneyLines.forEach(l => {
      if (!withEstimates && l.estimate) return;
      const k = l.category;
      if (!m.has(k)) m.set(k, { key: k, category: k, items: 0, estimates: 0, total: 0, paid: 0, balance: 0, fromServices: 0 });
      const g = m.get(k);
      g.items += 1; g.total += l.total; g.paid += l.paid; g.balance += l.balance;
      if (l.estimate) g.estimates += 1;
      if (l.source === 'service') g.fromServices += 1;
    });
    const order = (c) => { const i = EXPENSE_CATEGORIES.indexOf(c); return i < 0 ? 999 : i; };
    return [...m.values()]
      .map(g => ({ ...g, total: round2(g.total), paid: round2(g.paid), balance: round2(g.balance), on: !catOff.has(g.key) }))
      .sort((a, b) => order(a.category) - order(b.category) || a.category.localeCompare(b.category));
  }, [moneyLines, withEstimates, catOff]);

  const moneyShown = useMemo(() => moneyRows.filter(r => r.on), [moneyRows]);
  const moneyTotals = useMemo(() => moneyShown.reduce((t, r) => ({
    total: round2(t.total + r.total), paid: round2(t.paid + r.paid), balance: round2(t.balance + r.balance),
    estimates: t.estimates + r.estimates,
  }), { total: 0, paid: 0, balance: 0, estimates: 0 }), [moneyShown]);

  // ── The report (screen = file) ──────────────────────────────────────────
  const fnNote = [
    fnOff.size ? `Functions: ${shownFunctions.map(f => f.FunctionName).join(', ') || 'none'}` : '',
    pastFunctions.length ? `${pastFunctions.map(f => `${f.FunctionName} (${fmtDate(f.FunctionDate)})`).join(', ')} ${pastFunctions.length === 1 ? 'is' : 'are'} over - not counted` : '',
  ].filter(Boolean).join(' · ');
  const catNote = catOff.size ? `Costs: ${moneyShown.map(r => r.category).join(', ') || 'none'}` : '';

  const memberColumns = useMemo(() => [
    { key: 'FunctionName', label: 'Function', pdfWidth: 46 },
    { key: 'date', label: 'Date', pdfWidth: 24 },
    { key: 'time', label: 'Time', pdfWidth: 26 },
    { key: 'parties', label: 'Parties', align: 'num', pdfWidth: 18 },
    { key: 'named', label: 'Named persons', align: 'num', pdfWidth: 22 },
    { key: 'extra', label: 'Extra-card persons', align: 'num', pdfWidth: 22 },
    { key: 'persons', label: 'Total persons', align: 'num', pdfWidth: 22 },
    ...(twoSided ? [
      { key: 'groom', label: 'Groom side', align: 'num', pdfWidth: 20 },
      { key: 'bride', label: 'Bride side', align: 'num', pdfWidth: 20 },
    ] : []),
  ], [twoSided]);

  const invitedByColumns = useMemo(() => [
    { key: 'name', label: 'Invited by', pdfWidth: 44, render: (r) => `${r.name}${r.isGuest ? ' (guest)' : ''}` },
    { key: 'mobile', label: 'Mobile', pdfWidth: 24 },
    { key: 'guests', label: 'Guests', align: 'num', pdfWidth: 16 },
    { key: 'persons', label: 'Persons', align: 'num', pdfWidth: 18 },
    { key: 'totalCards', label: 'Cards', align: 'num', pdfWidth: 16 },
    ...shownFunctions.map(f => ({
      key: `fn${f.FunctionId}`, label: f.FunctionName, align: 'num', pdfWidth: 18, keepEmpty: true,
      exportValue: (r) => String(r.perFn[String(f.FunctionId)] || 0),
      render: (r) => r.perFn[String(f.FunctionId)] || 0,
    })),
  ], [shownFunctions]);

  const cardColumns = useMemo(() => [
    { key: 'name', label: 'Card / insert', pdfWidth: 46 },
    { key: 'kind', label: 'Kind', pdfWidth: 18 },
    { key: 'forFunctions', label: 'Given for', pdfWidth: 60 },
    { key: 'households', label: 'Households', align: 'num', pdfWidth: 22 },
    { key: 'extra', label: 'Extra cards', align: 'num', pdfWidth: 20 },
    { key: 'total', label: 'To order', align: 'num', pdfWidth: 20 },
  ], []);

  const moneyColumns = useMemo(() => [
    { key: 'category', label: 'Category', pdfWidth: 46 },
    { key: 'items', label: 'Items', align: 'num', pdfWidth: 16 },
    { key: 'total', label: 'Amount', align: 'num', pdfWidth: 26, exportValue: (r) => money(r.total) },
    { key: 'paid', label: 'Paid in advance', align: 'num', pdfWidth: 26, exportValue: (r) => money(r.paid) },
    { key: 'balance', label: 'Balance to pay', align: 'num', pdfWidth: 26, exportValue: (r) => money(r.balance) },
    { key: 'estimates', label: 'Still estimates', align: 'num', pdfWidth: 20, exportValue: (r) => (r.estimates ? String(r.estimates) : '') },
  ], []);

  const summary = useMemo(() => [
    biggest ? { label: `Most persons (${biggest.FunctionName})`, value: biggest.persons } : null,
    { label: 'Cards to order', value: cardStats.toOrder },
    { label: 'Paid in advance', value: money(moneyTotals.paid) },
    { label: 'Balance to pay', value: money(moneyTotals.balance) },
  ].filter(Boolean), [biggest, cardStats, moneyTotals]);

  const report = useMemo(() => {
    const sections = [];
    if (blocks.members) sections.push({
      title: BLOCK_LABELS.members, note: fnNote || undefined,
      columns: memberColumns, rows: memberShown,
      totalsRow: {
        FunctionName: 'Total', parties: memberTotals.parties, named: memberTotals.named,
        extra: memberTotals.extra, persons: memberTotals.persons,
        groom: memberTotals.groom, bride: memberTotals.bride,
      },
    });
    if (blocks.invitedBy) sections.push({
      title: BLOCK_LABELS.invitedBy, columns: invitedByColumns, rows: invitedByRows,
      totalsRow: {
        name: 'Total', guests: invitedByTotals.guests, persons: invitedByTotals.persons,
        totalCards: invitedByTotals.totalCards,
        ...Object.fromEntries(shownFunctions.map(f => [`fn${f.FunctionId}`,
          invitedByRows.reduce((s, r) => s + (r.perFn[String(f.FunctionId)] || 0), 0)])),
      },
    });
    if (blocks.cards) sections.push({
      title: BLOCK_LABELS.cards,
      note: [`${cardStats.households} households`,
        cardStats.extraCards ? `${cardStats.extraCards} extra cards (${cardStats.extraPersons} persons)` : '',
        cardStats.ownFamily ? `${cardStats.ownFamily} own family — no card` : '',
        cardStats.withoutType ? `${cardStats.withoutType} households with no card type yet` : ''].filter(Boolean).join(' · '),
      columns: cardColumns, rows: cardStats.rows,
      totalsRow: {
        name: 'Total to order', households: cardStats.rows.reduce((s, r) => s + r.households, 0),
        extra: cardStats.rows.reduce((s, r) => s + r.extra, 0), total: cardStats.toOrder,
      },
    });
    if (blocks.money) sections.push({
      title: BLOCK_LABELS.money,
      note: [catNote, withEstimates ? 'estimates included' : 'estimates left out'].filter(Boolean).join(' · '),
      columns: moneyColumns, rows: moneyShown,
      totalsRow: {
        category: 'Total', total: money(moneyTotals.total), paid: money(moneyTotals.paid),
        balance: money(moneyTotals.balance), estimates: moneyTotals.estimates || '',
      },
    });
    return {
      title: 'Brief Report',
      subtitle: event?.EventName || '',
      summary,
      sections,
    };
  }, [blocks, fnNote, catNote, withEstimates, event, summary,
    memberColumns, memberShown, memberTotals,
    invitedByColumns, invitedByRows, invitedByTotals, shownFunctions,
    cardColumns, cardStats, moneyColumns, moneyShown, moneyTotals]);

  return {
    canView, loading, event, reload: load, twoSided,
    blocks, toggleBlock, blockLabels: BLOCK_LABELS,
    functions: liveFunctions, pastFunctions, fnOff, toggleFunction, shownFunctions,
    catOff, toggleCategory, withEstimates, setWithEstimates,
    memberRows, memberShown, memberTotals, memberColumns, biggest,
    invitedByRows, invitedByTotals, invitedByColumns,
    cardStats, cardColumns,
    moneyRows, moneyShown, moneyTotals, moneyColumns,
    summary, report, money,
  };
}
